import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, X } from "lucide-react";

interface EmptyColumnsDialogProps {
  /** Whether the warning is showing. */
  open: boolean;
  /** Empty metadata columns offered as drop candidates. */
  columns: string[];
  /** Proceed with the upload, dropping `droppedColumns` ([] keeps everything). */
  onResolve: (droppedColumns: string[]) => void;
  /** Dismiss and return to the form without submitting. */
  onCancel: () => void;
}

/**
 * Shown on plot-map upload submit when entirely-empty metadata columns are found.
 * Every empty column starts slated to drop; clicking the ✕ on a chip rescues it
 * (keeps it). "Keep all columns" drops nothing; "Drop these N columns" drops
 * whatever is still listed; once every column is rescued the action becomes a
 * plain "Continue". Modeled on {@link InvalidPlotIDsDialog} (forced decision —
 * dismiss only via the footer actions or Escape).
 */
export const EmptyColumnsDialog: React.FC<EmptyColumnsDialogProps> = ({
  open,
  columns,
  onResolve,
  onCancel,
}) => {
  const [remaining, setRemaining] = useState<string[]>(columns);

  // Reset the drop list to the full set each time the dialog opens.
  useEffect(() => {
    if (open) setRemaining(columns);
  }, [open, columns]);

  const rescue = (column: string) =>
    setRemaining((prev) => prev.filter((c) => c !== column));

  const dropCount = remaining.length;
  const dropLabel = dropCount === 1 ? "Drop this column" : `Drop these ${dropCount} columns`;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-2xl"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          onCancel();
        }}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Empty columns found
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            These metadata columns have no data in any row, so they won't add any information to
            your plots. Drop them, or remove a column from the list (✕) to keep it.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {dropCount > 0 ? (
          <div className="flex flex-wrap gap-2 py-1">
            {remaining.map((column) => (
              <Badge key={column} variant="secondary" className="gap-1 pl-2 pr-1 text-sm font-normal">
                <span className="font-mono">{column}</span>
                <button
                  type="button"
                  aria-label={`Keep column ${column}`}
                  onClick={() => rescue(column)}
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-muted-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="py-1 text-sm italic text-muted-foreground">
            Every column will be kept.
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          {dropCount > 0 ? (
            <>
              <AlertDialogAction
                onClick={() => onResolve([])}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80">
                Keep all columns
              </AlertDialogAction>
              <AlertDialogAction onClick={() => onResolve(remaining)}>
                {dropLabel}
              </AlertDialogAction>
            </>
          ) : (
            <AlertDialogAction onClick={() => onResolve([])}>Continue</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
